import {useEffect} from 'react';
import classes from 'src/theme.module.css';

/**
 * Keeps every themed `Tabs.List` usable while it is a single scrollable row:
 * scrolls the active tab into view and flags which edge still has tabs.
 */

const boundLists = new WeakSet<HTMLElement>();
const lastActiveTab = new WeakMap<HTMLElement, Element>();

const collectLists = (): HTMLElement[] =>
  Array.from(document.querySelectorAll<HTMLElement>(`.${classes.tabsList}`));

const markOverflow = (list: HTMLElement) => {
  const overflow = list.scrollWidth - list.clientWidth;
  const hasOverflow = overflow > 1;
  list.toggleAttribute('data-overflow-start', hasOverflow && list.scrollLeft > 1);
  list.toggleAttribute(
    'data-overflow-end',
    hasOverflow && list.scrollLeft < overflow - 1
  );
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

/**
 * Picks the scroll offset that keeps the active tab fully visible AND puts a
 * tab boundary on the leading edge, so no neighbour is sliced mid-glyph.
 */
const scrollActiveIntoView = (list: HTMLElement, active: Element) => {
  const maxScroll = list.scrollWidth - list.clientWidth;
  if (maxScroll <= 1) return;

  const listLeft = list.getBoundingClientRect().left;
  const current = list.scrollLeft;
  const tabBox = active.getBoundingClientRect();

  const atLeast = current + tabBox.right - listLeft - list.clientWidth;
  const atMost = current + tabBox.left - listLeft;
  const min = clamp(atLeast, 0, maxScroll);
  const max = clamp(Math.max(atMost, atLeast), 0, maxScroll);

  const boundaries = [0, maxScroll].concat(
    Array.from(list.children).map(
      child => current + child.getBoundingClientRect().left - listLeft
    )
  );
  const usable = boundaries.filter(
    offset => offset >= min - 1 && offset <= max + 1
  );
  const target = usable.length
    ? usable.reduce((best, offset) =>
        Math.abs(offset - current) < Math.abs(best - current) ? offset : best
      )
    : clamp(current, min, max);

  list.scrollLeft = Math.round(clamp(target, 0, maxScroll));
};

const alignAll = (force: boolean) => {
  collectLists().forEach(list => {
    if (!boundLists.has(list)) {
      boundLists.add(list);
      list.addEventListener('scroll', () => markOverflow(list), {passive: true});
    }

    const active = list.querySelector('[data-active]');
    const activeChanged = active !== lastActiveTab.get(list);
    if (active) {
      lastActiveTab.set(list, active);
      if (force || activeChanged) scrollActiveIntoView(list, active);
    }

    markOverflow(list);
  });
};

export const useTabsScrollAffordance = () => {
  useEffect(() => {
    let frame = 0;
    const schedule = (force = false) => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = 0;
        alignAll(force);
      });
    };

    schedule(true);

    const observer = new MutationObserver(() => schedule());
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributeFilter: ['data-active'],
    });

    const handleResize = () => schedule(true);
    window.addEventListener('resize', handleResize);
    void document.fonts?.ready.then(() => schedule(true));

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
};
